import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Copy } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { ProIncludedPill } from '@/components/ui/pro-pill';
import { MetaBlock } from '@/components/channels/MetaBlock';
import { ChannelPotentialBoost } from '@/components/channels/PotentialGauge';
import { ChannelSyncStatus } from '@/components/channels/ChannelSyncStatus';
import { ChannelOrders } from '@/components/channels/ChannelOrders';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { GmcIssuesCard } from '@/pages/channels/components/GmcIssuesCard';
import { FeedUrlCard } from '@/pages/channels/components/FeedUrlCard';
import { VisibilityHeaderCard } from '@/pages/inventory/components/VisibilityHeaderCard';
import { InvisibleProductsCard } from '@/pages/inventory/components/InvisibleProductsCard';
import { ProfileCompletenessBar } from '@/pages/inventory/components/ProfileCompletenessBar';
import { NotFound } from '@/pages/errors/NotFound';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useGoBack } from '@/hooks/useGoBack';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { useGbpTotals, useInsightsTotals, useLocalAds } from '@/hooks/useInsights';
import { useTier } from '@/lib/tier';
import { buildChannel, IN_SCOPE_CHANNEL_IDS } from '@/lib/channels';
import { DISCONNECTED_THRESHOLD_DAYS, daysSince, deriveConnectionState } from '@/lib/connection-state';
import { copyToClipboard } from '@/lib/utils';

function ManageLoading() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-10 w-72" />
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
				<Skeleton className="h-48" />
				<Skeleton className="h-64" />
			</div>
		</div>
	);
}

/**
 * Pure helper behind the header pill's "Live" / "Issues" flip on the
 * ChannelManage page (NEA-5942). Lifted so the four input combinations
 * (GMC all-disapproved / empty feed / stale ingest / clean) are table-testable
 * and the load-bearing `showSyncStatus` Reservations gate can't silently
 * regress.
 *
 * `showSyncStatus` is `!isReservations` at the call site — Reservations is
 * an order surface with no upstream stock feed, so freshness must NOT flip
 * the pill there. Other branches still fire (e.g. all-disapproved on a
 * Reservations channel still reads "Issues") because they aren't ingest
 * signals.
 */
export function computeChannelHasIssues({ summary, summaryLoading, showSyncStatus }) {
	if (summaryLoading || summary == null) return false;
	const gmcStats = summary.gmcProductStats;
	if (gmcStats?.active === 0 && gmcStats?.disapproved > 0) return true;
	const totalItems = summary.totalItems ?? null;
	const inStockItems = summary.inStockItems ?? null;
	if (totalItems != null && totalItems === 0 && (inStockItems ?? 0) === 0) return true;
	if (showSyncStatus) {
		const days = daysSince(summary.source?.lastSyncAt);
		if (days != null && days >= DISCONNECTED_THRESHOLD_DAYS) return true;
	}
	return false;
}

function formatRelativeTime(value) {
	if (!value) return null;
	const ms = typeof value === 'number' ? value : Date.parse(value);
	if (!Number.isFinite(ms)) return null;
	const minutes = Math.floor((Date.now() - ms) / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? '' : 's'} ago`;
}

function formatDate(value) {
	if (!value) return null;
	const ms = typeof value === 'number' ? value : Date.parse(value);
	if (!Number.isFinite(ms)) return null;
	return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const formatTrend = (change) => {
	if (change == null || !Number.isFinite(Number(change))) return undefined;
	const value = Number(change);
	return `${value > 0 ? '+' : ''}${value}%`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-GB');

/**
 * 12-month performance KPIs for the channel, from real insights.
 *
 * Google rolls up the *whole* organic Google footprint, not just Local
 * Inventory listings (NEA-5879):
 *   - `Views`        = GBP profile views (`gmb`) + Local Listings impressions
 *   - `Engagements`  = GBP interactions (`gmb`) + Local Listings clicks
 *   - `Click-through rate` stays scoped to Local Listings (clicks /
 *     impressions on the listings funnel) — the GBP-inflated Views total
 *     above isn't a denominator a CTR could be computed against.
 *
 * Local Ads reads its own summary. Channels without an insights source
 * return `null` and the Performance card is omitted entirely (rather than
 * rendering fabricated zeros). The "See full results" link still lives
 * inside that card, so the Results-page pointer goes with it when the
 * card is hidden — the page's other sections (visibility, sync status,
 * channel meta) carry the page when there are no KPIs to show.
 */
function channelKpis(channel, listings, gbp, ads) {
	if (channel.id === 'google') {
		const hasListings = Boolean(listings?.hasData);
		const hasGbp = Boolean(gbp?.hasData);
		if (!hasListings && !hasGbp) return null;
		const totalViews = (gbp?.views || 0) + (listings?.impressions || 0);
		const totalEngagements = (gbp?.interactions || 0) + (listings?.clicks || 0);
		const kpis = [
			{ label: 'Views', value: formatNumber(totalViews) },
			{ label: 'Engagements', value: formatNumber(totalEngagements) }
		];
		if (hasListings && listings.impressions > 0) {
			kpis.push({
				label: 'Click-through rate',
				value: `${((listings.clicks / listings.impressions) * 100).toFixed(2)}%`
			});
		}
		return kpis;
	}
	if (channel.id === 'local-ads') {
		const summary = ads?.summary;
		if (!summary) return null;
		const kpis = [];
		if (summary.impressions != null)
			kpis.push({ label: 'Impressions', value: formatNumber(summary.impressions), trend: formatTrend(summary.impressionsChange) });
		if (summary.clicks != null)
			kpis.push({ label: 'Clicks', value: formatNumber(summary.clicks), trend: formatTrend(summary.clicksChange) });
		// No trend on cost-per-click — a rise is bad, so a green "+%" pill would mislead.
		if (summary.cpc != null) kpis.push({ label: 'Cost per click', value: `£${Number(summary.cpc).toFixed(2)}` });
		return kpis.length > 0 ? kpis : null;
	}
	return null;
}

export function ChannelManage() {
	const { id = '' } = useParams();
	const navigate = useNavigate();
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { disable, isBusy } = useChannelActions(shop?.id);
	const { billing } = useBillingStatus(shop?.id);
	const { shopPath } = useShopPath();
	const goBack = useGoBack('/channels');
	const { isPro } = useTier();
	const { summary, isLoading: summaryLoading } = useInventoryPageSummary(shop?.id, id);
	const listings = useInsightsTotals(id === 'google' ? shop?.id : null, 'local_listings');
	const gbp = useGbpTotals(id === 'google' ? shop?.id : null);
	const { data: ads } = useLocalAds(id === 'local-ads' ? shop?.id : null);
	const [copied, setCopied] = useState(false);

	if (!IN_SCOPE_CHANNEL_IDS.includes(id)) return <NotFound />;
	if (!shop?.id || channelsLoading || defsLoading) return <ManageLoading />;

	const channel = buildChannel(id, definitions, channels, billing?.entitlements);
	if (!channel) return <NotFound />;
	// Manage is only meaningful for a live channel — otherwise send the
	// retailer to the detail page where they can switch it on.
	if (!channel.live) return <Navigate to={shopPath(`/channels/${id}`)} replace />;

	const isLocalAds = channel.id === 'local-ads';
	const isStorefront = channel.id === 'storefront';
	const isReservations = channel.id === 'product-reservations';
	const isOrderChannel = isReservations || channel.id === 'local-checkout';
	// Order channels carry per-channel settings (notification emails, SMS,
	// email templates) that live on their own sub-route (NEA-6015).
	const hasSettingsPage = isOrderChannel;
	// Reservations is an order surface, not a feed — no POS sync card for it.
	const showSyncStatus = !isReservations;
	const busy = isBusy(channel.id);

	const totalItems = summary?.totalItems ?? null;
	const inStockItems = summary?.inStockItems ?? null;
	const channelStatusKnown = !summaryLoading && summary != null;
	const channelHasIssues = computeChannelHasIssues({ summary, summaryLoading, showSyncStatus });
	const lastSync = formatRelativeTime(summary?.source?.lastSyncAt);
	const connectedSince = formatDate(channel.record?.createdAt);
	const profileCompleteness = summary?.profileCompleteness ?? [];
	// Show the priority-fix card whenever any profile attribute has a gap —
	// the card rotates through title → image → description → brand → category
	// so it stays useful even after titles are complete (NEA-5840).
	const hasPriorityFix = profileCompleteness.some((seg) => (seg.missingCount ?? 0) > 0);
	// A frozen feed flips the visibility card into its data-outdated state
	// rather than presenting last-sync counts as live (NEA-5934).
	const connectionState = deriveConnectionState({ source: summary?.source });
	const staleDays = connectionState.needsReconnect ? connectionState.daysSinceSync : null;
	const kpis = channelKpis(channel, listings, gbp, ads);

	const storefrontUrl = shop.slug ? `https://near.st/shop/${shop.slug}` : null;
	const copyStorefrontLink = async () => {
		if (!storefrontUrl) return;
		if (await copyToClipboard(storefrontUrl)) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
		}
	};

	const worksWith = (channel.worksWith ?? [])
		.map((worksWithId) => ({ id: worksWithId, definition: definitions[worksWithId] }))
		.filter((entry) => entry.definition);

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header className="flex flex-wrap items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-12 w-12" />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">{channel.title}</h1>
						{channelStatusKnown ? (
							<span
								className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${channelHasIssues ? 'bg-warning-soft text-warning-soft-foreground' : 'bg-success-soft text-success-soft-foreground'}`}
							>
								{channelHasIssues ? null : (
									<span className="relative flex h-2 w-2">
										<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
										<span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
									</span>
								)}
								{channelHasIssues ? 'Issues' : 'Live'}
							</span>
						) : null}
						{channel.tag ? (
							<span className="rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-action-soft-foreground">
								{channel.tag}
							</span>
						) : null}
					</div>
					<p className="mt-1.5 max-w-3xl text-base text-muted-foreground">{channel.description}</p>
				</div>
			</header>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
				<div className="min-w-0 space-y-6">
					{showSyncStatus && summaryLoading ? <Skeleton className="h-[88px]" /> : null}
					{showSyncStatus && !summaryLoading ? (
						<ChannelSyncStatus
							channel={channel}
							lastSync={lastSync}
							lastSyncAt={summary?.source?.lastSyncAt}
							activeItems={inStockItems}
							totalItems={totalItems}
							gmcProductStats={summary?.gmcProductStats}
						/>
					) : null}

					<FeedUrlCard channel={channel} />

					{isOrderChannel ? <ChannelOrders shopId={shop.id} channelId={channel.id} /> : null}

					{kpis ? (
						<SoftCard className="space-y-5 !p-5 md:!p-6">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<div>
									<h3 className="text-[15px] font-bold text-foreground md:text-base">Performance · last 12 months</h3>
									<p className="mt-0.5 text-xs text-muted-foreground">
										How this channel is contributing to your local growth.
									</p>
								</div>
								<Link
									to={shopPath('/results')}
									className="inline-flex items-center gap-1.5 text-sm font-semibold text-action hover:underline"
								>
									See full results <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</div>
							<div className={`grid grid-cols-1 gap-3 ${kpis.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
								{kpis.map((kpi) => (
									<div
										key={kpi.label}
										className="flex flex-col items-center rounded-2xl border border-border bg-card p-5 text-center"
									>
										<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
											{kpi.label}
										</div>
										<div className="mt-3 text-3xl font-extrabold tracking-tight tabular-nums text-foreground md:text-4xl">
											{kpi.value}
										</div>
										{kpi.trend ? (
											<div className="mt-3 inline-flex items-center rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-soft-foreground">
												{kpi.trend}
											</div>
										) : null}
									</div>
								))}
							</div>
						</SoftCard>
					) : null}

					{summaryLoading ? (
						<section className="space-y-4">
							<div>
								<h3 className="text-[15px] font-bold text-foreground md:text-base">
									Product data showing in {channel.title}
								</h3>
								<p className="mt-0.5 text-xs text-muted-foreground">
									The richer your product data, the more of your catalogue {channel.title} will show.
								</p>
							</div>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<Skeleton className="h-36" />
								<Skeleton className="h-36" />
							</div>
							<Skeleton className="h-52" />
						</section>
					) : summary ? (
						<section className="space-y-4">
							<div>
								<h3 className="text-[15px] font-bold text-foreground md:text-base">
									Product data showing in {channel.title}
								</h3>
								<p className="mt-0.5 text-xs text-muted-foreground">
									The richer your product data, the more of your catalogue {channel.title} will show.
								</p>
							</div>
							{hasPriorityFix && channel.id !== 'google' ? (
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<VisibilityHeaderCard
										visibility={summary.visibility}
										inStockItems={inStockItems ?? 0}
										showChannelBreakdown={false}
										gmcProductStats={summary?.gmcProductStats}
										staleDays={staleDays}
									/>
									<InvisibleProductsCard profileCompleteness={profileCompleteness} inStockItems={inStockItems ?? 0} />
								</div>
							) : (
								<VisibilityHeaderCard
									visibility={summary.visibility}
									inStockItems={inStockItems ?? 0}
									showChannelBreakdown={false}
									gmcProductStats={summary?.gmcProductStats}
									staleDays={staleDays}
								/>
							)}
							{channel.id === 'google' && summary?.gmcProductStats?.disapprovedByReason?.length > 0 ? (
								<div className="pt-4">
									<SectionHeader
										eyebrow="Feed health"
										title="Why Google is rejecting some of your products"
										sub="Resolving these issues helps more of your catalogue reach shoppers on Google. You can download the impacted barcodes and bulk upload missing attributes via the inventory page."
									/>
									<GmcIssuesCard disapprovedByReason={summary?.gmcProductStats?.disapprovedByReason} shopId={shop.id} />
								</div>
							) : null}
							{channel.id === 'google' ? null : (
								<div className="pt-2">
									<h3 className="text-[15px] font-bold text-foreground md:text-base">
										Improve your product profiles to boost {channel.title}
									</h3>
									<p className="mt-0.5 mb-3 text-xs text-muted-foreground">
										Richer profiles increase how much of your catalogue reaches local shoppers.
									</p>
									<ProfileCompletenessBar profileCompleteness={profileCompleteness} />
								</div>
							)}
						</section>
					) : null}
				</div>

				<aside className="space-y-5">
					<SoftCard className="space-y-4 !p-5 md:!p-6">
						<MetaBlock label={isLocalAds ? 'Current budget' : 'Subscription'}>
							{isLocalAds ? (
								<div className="space-y-1.5">
									<div className="text-base font-bold tabular-nums text-foreground">
										{ads?.budget?.daily ? `£${ads.budget.daily} /day` : 'From £2 /day'}
										{ads?.budget?.monthly ? ` · £${Math.round(ads.budget.monthly)} /month` : ''}
									</div>
									<p className="text-xs leading-snug text-muted-foreground">
										Paid directly to Google. Unify holds a 15% ad management fee.
									</p>
								</div>
							) : channel.price && isPro ? (
								<div className="space-y-1.5">
									<ProIncludedPill />
									<p className="text-xs leading-snug text-muted-foreground">
										Worth £{channel.price}/month — covered by your Pro plan.
									</p>
								</div>
							) : channel.price ? (
								<span className="inline-flex items-baseline gap-1 text-base font-bold text-foreground">
									£{channel.price}
									<span className="text-xs font-medium text-muted-foreground">/month</span>
								</span>
							) : (
								<span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-1 text-sm font-semibold text-success-soft-foreground">
									Included with Unify
								</span>
							)}
						</MetaBlock>

						<ChannelPotentialBoost channelId={channel.id} />

						{connectedSince ? (
							<div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
								<span>Connected since</span>
								<span className="font-semibold tabular-nums text-foreground">{connectedSince}</span>
							</div>
						) : null}

						{isLocalAds ? (
							<Link
								to={shopPath('/ads')}
								className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-action px-4 py-2.5 text-sm font-semibold text-action-foreground transition hover:opacity-90"
							>
								Adjust budget <ArrowRight className="h-3.5 w-3.5" />
							</Link>
						) : isStorefront ? (
							<div className="space-y-2">
								{storefrontUrl ? (
									<button
										type="button"
										onClick={copyStorefrontLink}
										className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-muted px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent"
									>
										{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
										{copied ? 'Link copied' : 'Copy link to Local Storefront'}
									</button>
								) : null}
								<Link
									to={shopPath('/settings/store/storefront')}
									className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition hover:opacity-90"
								>
									Manage Local Storefront info <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</div>
						) : channel.alwaysLive ? null : hasSettingsPage ? (
							// LC + Reservations promote Channel settings to the primary CTA in
							// the sidebar (light-grey pill) and demote Disable to a quiet
							// inline link beneath it — destructive actions shouldn't outweigh
							// the everyday "edit my notification email" workflow on these
							// order channels.
							<div className="space-y-3">
								<Link
									to={shopPath(`/channels/${channel.id}/settings`)}
									className="inline-flex w-full items-center justify-center rounded-full bg-muted px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent"
								>
									Channel settings
								</Link>
								<button
									type="button"
									disabled={busy}
									onClick={() =>
										disable(channel.id)
											.then(() => navigate(shopPath(`/channels/${channel.id}`)))
											.catch(() => {})
									}
									className="block w-full text-center text-sm text-muted-foreground transition hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-70"
								>
									{busy ? 'Disabling…' : `Disable ${channel.title}`}
								</button>
							</div>
						) : (
							<button
								type="button"
								disabled={busy}
								onClick={() =>
									disable(channel.id)
										.then(() => navigate(shopPath(`/channels/${channel.id}`)))
										.catch(() => {})
								}
								className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-muted px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-70"
							>
								{busy ? 'Disabling…' : 'Disable channel'}
							</button>
						)}
					</SoftCard>

					<SoftCard className="space-y-5 !p-5 text-sm md:!p-6">
						{channel.category ? (
							<MetaBlock label="Category">
								<span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{channel.category}
								</span>
							</MetaBlock>
						) : null}

						{worksWith.length > 0 ? (
							<MetaBlock label="Works with">
								<ul className="space-y-1.5">
									{worksWith.map((entry) => (
										<li key={entry.id}>
											{IN_SCOPE_CHANNEL_IDS.includes(entry.id) ? (
												<Link to={shopPath(`/channels/${entry.id}`)} className="text-action hover:underline">
													{entry.definition.title}
												</Link>
											) : (
												<span className="text-foreground">{entry.definition.title}</span>
											)}
										</li>
									))}
								</ul>
							</MetaBlock>
						) : null}

						<MetaBlock label="Learn more">
							<Link
								to={shopPath(`/channels/${channel.id}`)}
								className="inline-flex items-center gap-1 text-action hover:underline"
							>
								View channel overview <ArrowUpRight className="h-3.5 w-3.5" />
							</Link>
						</MetaBlock>

						{Array.isArray(channel.helpArticleUrls) && channel.helpArticleUrls.length > 0 ? (
							<MetaBlock label="Support">
								<ul className="space-y-1.5">
									{channel.helpArticleUrls.map((article) => (
										<li key={article.url}>
											<a href={article.url} target="_blank" rel="noreferrer" className="text-action hover:underline">
												{article.title}
											</a>
										</li>
									))}
								</ul>
							</MetaBlock>
						) : null}
					</SoftCard>
				</aside>
			</div>
		</div>
	);
}
